import {useRef, useLayoutEffect, useState} from 'react';

import type {Media} from '@pexip/media';
import type {AnalyzerNodeInit, AudioGraph} from '@pexip/media-processor';
import {
    createAnalyzerGraphNode,
    createAudioGraph,
    createStreamSourceGraphNode,
} from '@pexip/media-processor';

const createGraphAnalyzer = (media: Media, fftSize?: number) => {
    if (!media.rawStream?.getAudioTracks().length) {
        return [undefined, undefined] as const;
    }
    const sourceNode = createStreamSourceGraphNode(media.rawStream);
    const analyzerNode = createAnalyzerGraphNode({
        fftSize,
    });
    const graph = createAudioGraph([[sourceNode, analyzerNode]]);
    return [graph, analyzerNode] as const;
};

/**
 *  Creates and returns `AnalyzerNodeInit` based on the main media's `rawStream`.
 *
 *  Note: The `main media` is used here and not the `preview media`, that used in Settings modal.
 *  `rawStream` is used here and not `mediaService.media.stream`, because the audio meter should
 *  keep measuring the sound, regardless if the main audio track is muted or not.
 */
export const useAnalyzer = (media: Media, fftSize?: number) => {
    const audioGraph = useRef<AudioGraph | undefined>();
    const [analyzer, setAnalyzer] = useState<AnalyzerNodeInit>();

    useLayoutEffect(() => {
        let ignore = false;
        const updateState = (media: Media) => {
            const [graph, analyzer] = createGraphAnalyzer(media, fftSize);
            audioGraph.current = graph;
            const analyzerNode = analyzer;
            if (!ignore) {
                setAnalyzer(analyzerNode);
            }
        };
        updateState(media);

        const cleanUp = () => {
            void audioGraph.current?.release?.();
            setAnalyzer(undefined);
        };

        return () => {
            cleanUp();
            ignore = true;
        };
    }, [fftSize, media]);

    return analyzer;
};
